import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from 'vue';
import WaitDialog from '@/components/WaitDialog.vue';
import UserNotification from '@/components/UserNotification.vue';
import { useTraceWorksheetAnalyzer } from '@/composables/TraceWorksheetAnalyzer/UseTraceWorksheetAnalyzer';
import { ActivityType, ActivityLine, AlgorithmExecution, RateKeyLookupDetail, RateKeyResultDetail } from '@/models/TraceWorksheetModels';


export default /*@__PURE__*/_defineComponent({
  __name: 'TraceWorksheetAnalysis',
  props: {
    isActive: { type: Boolean, required: true },
    docUrl: { type: String, required: true }
  },
  emits: ["closed"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;

const emit = __emit;

const activeDialog = ref(props.isActive);

// Reactive docUrl ref that updates when the prop changes
const docUrlRef = ref<string>(props.docUrl);

// Single watcher to ensure docUrlRef is updated before loadWorksheet fires
watch(
	[() => props.isActive, () => props.docUrl],
	([newActive, newUrl], [oldActive]) => {
		docUrlRef.value = newUrl;
		activeDialog.value = newActive;
		// Reset state and reload when dialog opens
		if (newActive && !oldActive) {
			resetState();
			loadWorksheet();
		}
	}
);

const {
	isLoading,
	errorMessage,
	parsedWorksheet,
	filters,
	stats,
	filteredAlgorithms,
	paginatedAlgorithms,
	distinctAlgorithmNames,
	distinctStates,
	distinctLinesOfBusiness,
	distinctRateKeyNames,
	itemsPerPage,
	currentPage,
	totalPages,
	clearFilters,
	loadWorksheet,
} = useTraceWorksheetAnalyzer(docUrlRef);

// Stats panel visibility
const statsVisible = ref<boolean>(true);

// Selection state
const selectedAlgorithms = ref<Set<number>>(new Set());
const showSelectedOnly = ref<boolean>(false);
const openPanels = ref<number[]>([]);

function resetState() {
	selectedAlgorithms.value = new Set();
	showSelectedOnly.value = false;
	openPanels.value = [];
	statsVisible.value = true;
	clearFilters();
}

function closeAnalyzer() {
	activeDialog.value = false;
	emit('closed');
}

const isSelected = (algo: AlgorithmExecution): boolean => {
	return selectedAlgorithms.value.has(algo.lineNumber);
};

const toggleSelection = (algo: AlgorithmExecution) => {
	const newSet = new Set(selectedAlgorithms.value);
	if (newSet.has(algo.lineNumber)) {
		newSet.delete(algo.lineNumber);
		if (newSet.size === 0) {
			showSelectedOnly.value = false;
		}
	} else {
		newSet.add(algo.lineNumber);
	}
	selectedAlgorithms.value = newSet;
};

const clearSelection = () => {
	selectedAlgorithms.value = new Set();
	showSelectedOnly.value = false;
	openPanels.value = [];
};

const enterSelectedView = () => {
	showSelectedOnly.value = true;
	openPanels.value = [...selectedAlgorithms.value];
};

const expandAllSelected = () => {
	openPanels.value = [...selectedAlgorithms.value];
};

const collapseAllSelected = () => {
	openPanels.value = [];
};

const displayedAlgorithms = computed(() => {
	if (showSelectedOnly.value && selectedAlgorithms.value.size > 0) {
		return filteredAlgorithms.value.filter(a => selectedAlgorithms.value.has(a.lineNumber));
	}
	return paginatedAlgorithms.value;
});

const conditionHeaders = [
	{ title: 'Attribute', key: 'attribute', sortable: false },
	{ title: 'Value', key: 'actualValue', sortable: false },
	{ title: 'Op', key: 'operator', sortable: false },
	{ title: 'Expected', key: 'expectedValue', sortable: false },
	{ title: 'Result', key: 'result', sortable: false },
];

const setHeaders = [
	{ title: 'Type', key: 'targetType', sortable: false },
	{ title: 'Target', key: 'target', sortable: false },
	{ title: 'Value', key: 'value', sortable: false },
];

const rateKeyResultHeaders = [
	{ title: 'Key Name', key: 'keyName', sortable: false },
	{ title: 'Lookup Value', key: 'lookupValue', sortable: false },
	{ title: 'Result', key: 'result', sortable: false },
];

const formHeaders = [
	{ title: 'Form #', key: 'formNumber', sortable: false },
	{ title: 'Edition', key: 'formEdition', sortable: false },
	{ title: 'Action', key: 'action', sortable: false },
	{ title: 'Location', key: 'location', sortable: false },
];

const arithmeticHeaders = [
	{ title: 'Attribute', key: 'attribute', sortable: false },
	{ title: 'Current', key: 'currentValue', sortable: false },
	{ title: 'Op', key: 'operator', sortable: false },
	{ title: 'Operand', key: 'operand', sortable: false },
	{ title: 'Result', key: 'result', sortable: false },
];

const evaluationHeaders = [
	{ title: 'Attribute', key: 'attribute', sortable: false },
	{ title: 'Value', key: 'value', sortable: false },
	{ title: 'Condition', key: 'condition', sortable: false },
	{ title: 'Qualifies', key: 'qualifies', sortable: false },
	{ title: 'Target', key: 'target', sortable: false },
];

const getActivitiesByType = (algo: AlgorithmExecution, type: ActivityType): ActivityLine[] => {
	return algo.activities.filter(a => a.type === type);
};

const getRateKeyGroups = (algo: AlgorithmExecution): { keyName: string; lookup: RateKeyLookupDetail | null; results: RateKeyResultDetail[] }[] => {
	const groupMap = new Map<string, { lookup: RateKeyLookupDetail | null; results: RateKeyResultDetail[] }>();
	const order: string[] = [];

	for (const act of algo.activities) {
		if (act.type === 'rateKeyLookup' && act.rateKeyLookup) {
			const name = act.rateKeyLookup.keyName;
			if (!groupMap.has(name)) {
				groupMap.set(name, { lookup: null, results: [] });
				order.push(name);
			}
			const group = groupMap.get(name);
			if (group) {
				group.lookup = act.rateKeyLookup;
			}
		}
		if (act.type === 'rateKeyResult' && act.rateKeyResult) {
			const name = act.rateKeyResult.keyName;
			if (!groupMap.has(name)) {
				groupMap.set(name, { lookup: null, results: [] });
				order.push(name);
			}
			const group = groupMap.get(name);
			if (group) {
				group.results.push(act.rateKeyResult);
			}
		}
	}

	return order
		.map(keyName => {
			const group = groupMap.get(keyName);
			if (!group) {
				return null;
			}

			return { keyName, ...group };
		})
		.filter((group): group is { keyName: string; lookup: RateKeyLookupDetail | null; results: RateKeyResultDetail[] } => group !== null);
};

const downloadFile = () => {
	if (docUrlRef.value) {
		window.location.href = docUrlRef.value;
	}
};

const highlightSearch = (text: string, searchTerm: string): string => {
	if (!searchTerm) return text;
	const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const regex = new RegExp(`(${escaped})`, 'gi');
	return text.replace(regex, '<mark>$1</mark>');
};

const __returned__ = { props, emit, activeDialog, docUrlRef, isLoading, errorMessage, parsedWorksheet, filters, stats, filteredAlgorithms, paginatedAlgorithms, distinctAlgorithmNames, distinctStates, distinctLinesOfBusiness, distinctRateKeyNames, itemsPerPage, currentPage, totalPages, clearFilters, loadWorksheet, statsVisible, selectedAlgorithms, showSelectedOnly, openPanels, resetState, closeAnalyzer, isSelected, toggleSelection, clearSelection, enterSelectedView, expandAllSelected, collapseAllSelected, displayedAlgorithms, conditionHeaders, setHeaders, rateKeyResultHeaders, formHeaders, arithmeticHeaders, evaluationHeaders, getActivitiesByType, getRateKeyGroups, downloadFile, highlightSearch, WaitDialog, UserNotification }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})